import React, { useEffect, useState } from "react";
import {
  MdBusiness,
  MdPayments,
  MdWarning,
  MdCheckCircle,
  MdAccessTime,
  MdStorefront
} from "react-icons/md";

export default function Dashboard() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await fetch(
        "https://api.visiontrackcrm.com/registration/stores"
      );
      const data = await response.json();
      if (data.success) {
        setStores(data.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const totalStores = stores.length;
  const activeStores = stores.filter(
    item => String(item.subscription_status || "").toUpperCase() === "ACTIVE"
  ).length;
  const pendingApprovals = stores.filter(
    item => String(item.subscription_status || "").toUpperCase() === "PENDING"
  ).length;
  const deactivatedStores = stores.filter(
    item => {
      const s = String(item.subscription_status || "").toUpperCase();
      return s === "DEACTIVATED" || s === "INACTIVE";
    }
  ).length;
  const totalRevenue = stores.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );

  const cards = [
    {
      title: "Total Stores",
      value: totalStores,
      icon: <MdBusiness />,
      bg: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
      color: "#0067B1"
    },
    {
      title: "Active Stores",
      value: activeStores,
      icon: <MdCheckCircle />,
      bg: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
      color: "#059669"
    },
    {
      title: "Pending Approvals",
      value: pendingApprovals,
      icon: <MdAccessTime />,
      bg: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
      color: "#dc2626"
    },
    {
      title: "Deactivated Stores",
      value: deactivatedStores,
      icon: <MdWarning />,
      bg: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
      color: "#ea580c"
    },
    {
      title: "Monthly Revenue",
      value: `₹ ${totalRevenue.toLocaleString()}`,
      icon: <MdPayments />,
      bg: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
      color: "#16a34a"
    }
  ];

  return (
    <div className="dashboard-container">
      <style>{`
        .dashboard-container {
          width: 100%;
        }

        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 20px;
          margin-bottom: 28px;
        }

        .dash-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 22px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
          transition: all 0.25s ease;
        }

        .dash-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 24px rgba(0, 103, 177, 0.08);
          border-color: #cbd5e1;
        }

        .card-meta h4 {
          font-size: 0.85rem;
          color: #64748b;
          font-weight: 600;
          margin-bottom: 6px;
        }

        .card-meta h2 {
          font-size: 1.6rem;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
        }

        .card-icon-badge {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.6rem;
        }

        .section-grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 24px;
        }

        .content-box {
          background: #ffffff;
          border-radius: 16px;
          padding: 24px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
        }

        .box-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 18px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f1f5f9;
        }

        .box-header h3 {
          font-size: 1.1rem;
          color: #0f172a;
          font-weight: 700;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .table-row-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px;
          border-radius: 10px;
          margin-bottom: 8px;
          background: #f8fafc;
          border: 1px solid #f1f5f9;
          transition: background 0.2s ease;
        }

        .table-row-item:hover {
          background: #f1f5f9;
        }

        .store-info-title {
          font-size: 0.92rem;
          font-weight: 600;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .expiry-badge {
          font-size: 0.8rem;
          font-weight: 700;
          color: #d97706;
          background: #fffbeb;
          padding: 4px 10px;
          border-radius: 9999px;
          border: 1px solid #fde68a;
        }

        .loading-container {
          padding: 40px;
          text-align: center;
          color: #64748b;
          font-size: 1.1rem;
          font-weight: 600;
        }

        @media(max-width: 1024px) {
          .section-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {loading ? (
        <div className="content-box loading-container">
          Loading Dashboard Data...
        </div>
      ) : (
        <>
          <div className="cards-grid">
            {cards.map((item, index) => (
              <div className="dash-card" key={index}>
                <div className="card-meta">
                  <h4>{item.title}</h4>
                  <h2>{item.value}</h2>
                </div>
                <div
                  className="card-icon-badge"
                  style={{ background: item.bg, color: item.color }}
                >
                  {item.icon}
                </div>
              </div>
            ))}
          </div>

          <div className="section-grid">
            {/* Recent Stores */}
            <div className="content-box">
              <div className="box-header">
                <h3>
                  <MdStorefront style={{ color: "#0067B1" }} />
                  Recent Stores
                </h3>
              </div>

              {stores.slice(0, 5).map(item => (
                <div className="table-row-item" key={item.id}>
                  <span className="store-info-title">
                    <MdBusiness style={{ color: "#94a3b8" }} />
                    {item.store_name}
                  </span>
                  <span
                    className={`badge ${
                      item.subscription_status === "ACTIVE"
                        ? "badge-active"
                        : "badge-danger"
                    }`}
                  >
                    {item.subscription_status}
                  </span>
                </div>
              ))}
            </div>

            {/* Expiring Subscriptions */}
            <div className="content-box">
              <div className="box-header">
                <h3>
                  <MdAccessTime style={{ color: "#d97706" }} />
                  Expiring Subscriptions
                </h3>
              </div>

              {stores
                .filter(item => item.expiry_date)
                .slice(0, 5)
                .map(item => {
                  const daysLeft = Math.ceil(
                    (new Date(item.expiry_date) - new Date()) /
                      (1000 * 60 * 60 * 24)
                  );
                  return (
                    <div className="table-row-item" key={item.id}>
                      <span className="store-info-title">{item.store_name}</span>
                      <span className="expiry-badge">{daysLeft} Days</span>
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}